/**
 * @fileoverview Filter Interface Component
 * 
 * This component renders the main filter interface with different layout options
 * and handles user interactions for setting filter values.
 * 
 * Requirements: 3.3, 3.4
 */

import React, { useState, useCallback } from 'react';
import { FilterInput } from './FilterInput.jsx';
import { FilterOperatorSelect } from './FilterOperatorSelect.jsx';

/**
 * Filter Interface Component
 * 
 * @param {Object} props - Component props
 * @param {FilterConfig} props.config - Filter configuration
 * @param {FilterDefinition[]} props.filterDefinitions - Available filter definitions
 * @param {ActiveFilter[]} props.activeFilters - Currently active filters
 * @param {function(string, *, FilterOperator): void} props.onFilterChange - Filter change handler
 * @param {function(): void} props.onReset - Reset handler
 * @param {boolean} props.loading - Loading state
 * @param {string} props.testId - Test ID
 */
export const FilterInterface = ({
  config,
  filterDefinitions,
  activeFilters,
  onFilterChange,
  onReset,
  loading = false,
  testId = 'filter-interface'
}) => {
  const [expandedFilters, setExpandedFilters] = useState(new Set());

  /**
   * Get active filter for a given filter ID
   */
  const getActiveFilter = useCallback((filterId) => {
    return activeFilters.find(f => f.id === filterId);
  }, [activeFilters]);

  /**
   * Handle filter value change
   */
  const handleFilterValueChange = useCallback((filterId, value) => {
    const activeFilter = getActiveFilter(filterId);
    const operator = activeFilter?.operator;
    onFilterChange(filterId, value, operator);
  }, [getActiveFilter, onFilterChange]);

  /**
   * Handle filter operator change
   */
  const handleOperatorChange = useCallback((filterId, operator) => {
    const activeFilter = getActiveFilter(filterId);
    const value = activeFilter?.value;
    onFilterChange(filterId, value, operator);
  }, [getActiveFilter, onFilterChange]);

  /**
   * Toggle filter expansion (for dropdown layout)
   */
  const toggleFilterExpansion = useCallback((filterId) => {
    setExpandedFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filterId)) {
        newSet.delete(filterId);
      } else {
        newSet.add(filterId);
      }
      return newSet;
    });
  }, []);

  /**
   * Render filter controls based on layout
   */
  const renderFilterControls = () => {
    const layoutClass = `filter-layout-${config.layout}`;
    
    switch (config.layout) {
      case 'dropdown':
        return (
          <div className={`filter-dropdown ${layoutClass}`}>
            <div className="filter-dropdown-header">
              <button
                className="filter-dropdown-toggle"
                onClick={() => toggleFilterExpansion('all')}
                disabled={loading}
                data-testid={`${testId}-dropdown-toggle`}
              >
                <span>Filters</span>
                <span className="filter-count">
                  {activeFilters.length > 0 && `(${activeFilters.length})`}
                </span>
                <svg className="dropdown-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            {expandedFilters.has('all') && (
              <div className="filter-dropdown-content">
                {renderFilterList()}
              </div>
            )}
          </div>
        );

      case 'vertical':
        return (
          <div className={`filter-vertical ${layoutClass}`}>
            {renderFilterList()}
          </div>
        );

      case 'horizontal':
      default:
        return (
          <div className={`filter-horizontal ${layoutClass}`}>
            {renderFilterList()}
          </div>
        );
    }
  };

  /**
   * Render the list of filter controls
   */
  const renderFilterList = () => {
    return (
      <div className="filter-list">
        {filterDefinitions.map((filterDef) => {
          const activeFilter = getActiveFilter(filterDef.id);
          const isActive = !!activeFilter;
          
          return (
            <div
              key={filterDef.id}
              className={`filter-item ${isActive ? 'active' : ''} ${filterDef.disabled ? 'disabled' : ''}`}
              data-testid={`${testId}-item-${filterDef.id}`}
            >
              <div className="filter-item-header">
                <label className="filter-label">
                  {filterDef.label}
                  {filterDef.required && <span className="required">*</span>}
                </label>
                
                {filterDef.description && (
                  <div className="filter-description">
                    {filterDef.description}
                  </div>
                )}
              </div>

              <div className="filter-item-controls">
                {/* Operator Selection (if multiple operators available) */}
                {filterDef.operators && filterDef.operators.length > 1 && (
                  <FilterOperatorSelect
                    operators={filterDef.operators}
                    selectedOperator={activeFilter?.operator}
                    onOperatorChange={(operator) => handleOperatorChange(filterDef.id, operator)}
                    disabled={loading || filterDef.disabled}
                    testId={`${testId}-operator-${filterDef.id}`}
                  />
                )}

                {/* Filter Input */}
                <FilterInput
                  definition={filterDef}
                  value={activeFilter?.value}
                  operator={activeFilter?.operator}
                  onChange={(value) => handleFilterValueChange(filterDef.id, value)}
                  disabled={loading || filterDef.disabled}
                  testId={`${testId}-input-${filterDef.id}`}
                />

                {/* Clear Individual Filter */}
                {isActive && (
                  <button
                    className="filter-clear-button"
                    onClick={() => handleFilterValueChange(filterDef.id, null)}
                    disabled={loading}
                    title={`Clear ${filterDef.label} filter`}
                    data-testid={`${testId}-clear-${filterDef.id}`}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`filter-interface ${loading ? 'loading' : ''}`} data-testid={testId}>
      {/* Filter Controls */}
      {renderFilterControls()}

      {/* Action Buttons */}
      <div className="filter-actions">
        {config.showClearAll && activeFilters.length > 0 && (
          <button
            className="filter-reset-button"
            onClick={onReset}
            disabled={loading}
            data-testid={`${testId}-reset`}
          >
            Clear All Filters
          </button>
        )}

        {loading && (
          <div className="filter-loading-indicator">
            <div className="loading-spinner" />
            <span>Applying filters...</span>
          </div>
        )}
      </div>
    </div>
  );
};
